// Délibérations publiées d'une collectivité (lot L5-B, H7-01, D-223) : liste et texte public, lecture sans
// compte. Seuls le texte public, les totaux du vote et le suivi d'exécution d'un projet ou d'un budget sont
// reçus de l'API publique ; aucune note interne ni pièce de séance n'existe dans cette réponse.
import type { DeliberationPublique } from '@cc/contrats';
import { EtatVide } from '@cc/ui';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams } from 'react-router';
import { requete } from '../../../api/client';
import { EtatRequete } from '../../../commun/EtatRequete';
import { codeCollectiviteValide } from '../collectivite';
import { Bloc, EnTeteCitoyen, Ligne, Liste, TexteStatut } from '../commun';
import styles from '../citoyen.module.css';

interface ReponseListe {
  collectivite: { code: string; nom: string };
  elements: DeliberationPublique[];
}

const jour = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { dateStyle: 'long', timeZone: 'Africa/Libreville' } as Intl.DateTimeFormatOptions);

export function texteExecution(d: DeliberationPublique): string {
  const etat = d.execution.etat === 'executee' ? 'Exécutée' : 'En cours d\'exécution';
  return d.execution.objet ? `${etat} : ${d.execution.objet.libelle} (${d.execution.objet.etat.toLowerCase()})` : etat;
}

export function PageDeliberations() {
  const { code: brut } = useParams();
  const code = codeCollectiviteValide(brut);
  const liste = useQuery({
    queryKey: ['public', 'deliberations', code ?? ''],
    queryFn: ({ signal }) => requete<ReponseListe>(`/public/collectivites/${encodeURIComponent(code as string)}/deliberations`, { signal }),
    enabled: Boolean(code),
    staleTime: 60_000,
  });
  if (!code) return <Navigate to="/citoyen" replace />;
  const elements = liste.data?.elements ?? [];
  return (
    <>
      <EnTeteCitoyen titre="Délibérations du conseil" surTitre={liste.data?.collectivite.nom} sousTitre="Textes publiés après validation du procès-verbal et transmission à la tutelle." retour={`/citoyen/${code}`} libelleRetour="Accueil" />
      <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} fonction="Les délibérations publiées" lignes={4}>
        {elements.length === 0 ? (
          <EtatVide titre="Aucune délibération publiée"><p>Cette collectivité n&apos;a pas encore publié de délibération sur le portail.</p></EtatVide>
        ) : (
          <Liste libelle="Délibérations publiées">
            {elements.map((d) => (
              <Ligne key={d.id} href={`/citoyen/${code}/deliberations/${d.id}`} titre={`${d.objet}${d.fictif ? ' (fictif)' : ''}`} sousTitre={`${d.numero} · séance du ${jour(d.date_seance)}`} droite={<TexteStatut libelle={d.execution.etat === 'executee' ? 'Exécutée' : 'Adoptée'} ton={d.execution.etat === 'executee' ? 'ok' : 'info'} />} />
            ))}
          </Liste>
        )}
      </EtatRequete>
    </>
  );
}

export function PageDeliberation() {
  const { code: brut, id } = useParams();
  const code = codeCollectiviteValide(brut);
  const fiche = useQuery({
    queryKey: ['public', 'deliberation', code ?? '', id ?? ''],
    queryFn: ({ signal }) => requete<{ collectivite: { code: string; nom: string }; deliberation: DeliberationPublique }>(`/public/collectivites/${encodeURIComponent(code as string)}/deliberations/${encodeURIComponent(id as string)}`, { signal }),
    enabled: Boolean(code && id),
    retry: false,
  });
  if (!code) return <Navigate to="/citoyen" replace />;
  const d = fiche.data?.deliberation;
  return (
    <>
      <EnTeteCitoyen titre={d ? d.objet : 'Délibération'} surTitre={fiche.data?.collectivite.nom} sousTitre={d ? `${d.numero} · séance ${d.session.numero} du ${jour(d.date_seance)}` : undefined} retour={`/citoyen/${code}/deliberations`} libelleRetour="Délibérations" />
      <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} fonction="La délibération" lignes={4}>
        {d ? (
          <>
            <Bloc titre="Texte de la délibération">
              <p className={styles.aide}>{d.texte_public}</p>
            </Bloc>
            <Bloc titre="Vote">
              <p className={styles.aide}>{`Adoptée : ${d.totaux.pour} pour, ${d.totaux.contre} contre, ${d.totaux.abstention} abstention${d.totaux.abstention > 1 ? 's' : ''}.`}</p>
            </Bloc>
            <Bloc titre="Suivi d'exécution">
              <p className={styles.aide}>{texteExecution(d)}</p>
            </Bloc>
            <p className={styles.aide}>{`Publiée le ${jour(d.publiee_le)}.`}</p>
          </>
        ) : null}
      </EtatRequete>
    </>
  );
}
